<script>
import { getInventoryLedgerList } from '@/api/inventory.js'
import AppPagination from '@/components/common/AppPagination.vue'

const todayText = () => new Date().toISOString().substring(0, 10)
const monthAgoText = () => {
  const d = new Date()
  d.setMonth(d.getMonth() - 1)
  return d.toISOString().substring(0, 10)
}

export default {
  name: 'InventoryLedgerPage',
  components: { AppPagination },
  data() {
    return {
      search: {
        keyword: '',
        movementType: '',
        startDate: monthAgoText(),
        endDate: todayText(),
      },
      items: [],
      page: 1,
      size: 10,
      total: 0,
      loading: false,
      movementTypes: [
        { title: '전체', value: '' },
        { title: '본사 입고', value: 'HQ_INBOUND' },
        { title: '지점 위탁 입고', value: 'BRANCH_CONSIGNMENT_INBOUND' },
        { title: '지점 이동 출고', value: 'TRANSFER_OUT' },
        { title: '지점 이동 입고', value: 'TRANSFER_IN' },
        { title: '판매 불출', value: 'SALE_OUT' },
        { title: '판매 취소 입고', value: 'SALE_CANCEL_IN' },
        { title: '보관 입고', value: 'STORAGE_IN' },
        { title: '보관 출고', value: 'STORAGE_OUT' },
        { title: '수량 조정 입고', value: 'ADJUST_IN' },
        { title: '수량 조정 출고', value: 'ADJUST_OUT' },
      ],
    }
  },
  mounted() {
    this.fetchList()
  },
  methods: {
    async fetchList() {
      this.loading = true
      try {
        const res = await getInventoryLedgerList({
          ...this.search,
          page: this.page,
          size: this.size,
        })
        if (res.data?.code === 0) {
          this.items = res.data.data?.items || []
          this.total = res.data.data?.total || 0
        }
      } finally {
        this.loading = false
      }
    },
    handleSearch() {
      this.page = 1
      this.fetchList()
    },
    handleReset() {
      this.search = { keyword: '', movementType: '', startDate: monthAgoText(), endDate: todayText() }
      this.page = 1
      this.fetchList()
    },
    formatDateTime(value) {
      return value ? String(value).replace('T', ' ').substring(0, 16) : '-'
    },
    formatQty(value) {
      if (value == null || value === '') return '-'
      return Number(value).toLocaleString(undefined, { maximumFractionDigits: 3 })
    },
    formatAmount(value) {
      if (value == null || value === '') return '-'
      return `${Number(value).toLocaleString()}원`
    },
    movementText(value) {
      const found = this.movementTypes.find(item => item.value === value)
      return found?.title || value || '-'
    },
    movementColor(value) {
      if (['HQ_INBOUND', 'BRANCH_CONSIGNMENT_INBOUND', 'TRANSFER_IN', 'SALE_CANCEL_IN', 'STORAGE_IN', 'ADJUST_IN'].includes(value)) return 'success'
      if (['SALE_OUT', 'TRANSFER_OUT', 'STORAGE_OUT', 'ADJUST_OUT'].includes(value)) return 'warning'
      return 'grey'
    },
    onPageChange(page) {
      this.page = page
      this.fetchList()
    },
    onSizeChange(size) {
      this.size = size
      this.page = 1
      this.fetchList()
    },
  },
}
</script>

<template>
  <div>
    <div class="d-flex justify-space-between align-center mb-4">
      <div>
        <h1 class="page-title">재고 원장</h1>
        <p class="page-desc">입고, 불출, 판매, 지점 이동, 조정 내역을 원장 기준으로 조회합니다.</p>
      </div>
    </div>

    <section class="search-panel">
      <div class="search-grid">
        <v-text-field v-model="search.startDate" type="date" label="시작일" variant="outlined" density="compact" hide-details />
        <v-text-field v-model="search.endDate" type="date" label="종료일" variant="outlined" density="compact" hide-details />
        <v-select v-model="search.movementType" :items="movementTypes" label="입출고 유형" variant="outlined" density="compact" hide-details />
        <v-text-field
          v-model="search.keyword"
          placeholder="주문번호, 상품명, 지점명"
          prepend-inner-icon="mdi-magnify"
          variant="outlined"
          density="compact"
          hide-details
          @keyup.enter="handleSearch"
        />
        <div class="search-actions">
          <v-btn color="primary" prepend-icon="mdi-magnify" @click="handleSearch">검색</v-btn>
          <v-btn variant="outlined" prepend-icon="mdi-refresh" @click="handleReset">초기화</v-btn>
        </div>
      </div>
    </section>

    <section class="list-panel">
      <div class="list-title">
        <span>재고 입출고 이력</span>
        <v-chip size="small" color="primary" variant="tonal">{{ total }}건</v-chip>
      </div>
      <v-progress-linear v-if="loading" indeterminate color="primary" />
      <v-table class="dark-table" density="compact">
        <thead>
          <tr>
            <th>발생일시</th>
            <th>유형</th>
            <th>상품</th>
            <th>지점</th>
            <th>상대 지점</th>
            <th class="text-right">증감수량</th>
            <th class="text-right">변경후 재고</th>
            <th class="text-right">금액</th>
            <th>주문번호</th>
            <th>메모</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="item in items" :key="item.inventoryLedgerCode">
            <td>{{ formatDateTime(item.createdDt) }}</td>
            <td><v-chip size="small" :color="movementColor(item.movementType)" variant="tonal">{{ movementText(item.movementType) }}</v-chip></td>
            <td><strong>{{ item.productName }}</strong></td>
            <td>{{ item.branchName || '-' }}</td>
            <td>{{ item.counterBranchName || '-' }}</td>
            <td class="text-right strong" :class="{ plus: Number(item.qtyDelta) > 0, minus: Number(item.qtyDelta) < 0 }">{{ formatQty(item.qtyDelta) }}</td>
            <td class="text-right">{{ formatQty(item.qtyAfter) }}</td>
            <td class="text-right">{{ formatAmount(item.amount) }}</td>
            <td>{{ item.orderCode || '-' }}</td>
            <td class="muted">{{ item.memo || '-' }}</td>
          </tr>
          <tr v-if="!loading && items.length === 0">
            <td colspan="10" class="empty-cell">재고 원장이 없습니다.</td>
          </tr>
        </tbody>
      </v-table>
      <AppPagination :total="total" :page="page" :size="size" @update:page="onPageChange" @update:size="onSizeChange" />
    </section>
  </div>
</template>

<style scoped>
.page-title { font-size: 24px; font-weight: 800; margin: 0; color: #f8fafc; }
.page-desc { margin: 6px 0 0; color: #a9b6c9; font-size: 13px; }
.search-panel, .list-panel { border: 1px solid #334155; background: #1c222e; border-radius: 8px; padding: 16px; }
.search-panel { margin-bottom: 16px; }
.search-grid { display: grid; grid-template-columns: 150px 150px 180px minmax(260px, 1fr) auto; gap: 10px; align-items: center; }
.search-actions { display: flex; gap: 8px; justify-content: flex-end; }
.list-title { display: flex; align-items: center; gap: 8px; font-size: 17px; font-weight: 800; color: #f8fafc; margin-bottom: 12px; }
.dark-table { border: 1px solid #334155; border-radius: 8px; overflow: hidden; background: #111827; }
.dark-table :deep(th) { background: #252d3b !important; color: #dbeafe !important; font-weight: 800 !important; white-space: nowrap; }
.dark-table :deep(td) { color: #e5e7eb !important; border-bottom: 1px solid #243041 !important; vertical-align: middle; }
.muted { color: #94a3b8; font-size: 12px; }
.strong { font-weight: 800; }
.plus { color: #22c55e !important; }
.minus { color: #f97316 !important; }
.empty-cell { text-align: center; color: #94a3b8 !important; padding: 36px !important; }
@media (max-width: 1100px) {
  .search-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .search-actions { justify-content: flex-start; }
}
</style>
